import { useShopI18n } from '../../i18n'
import type { CheckoutAddressForm } from '../flow'

type Props = {
  form: CheckoutAddressForm
  formIdPrefix: string
  onFieldChange: (field: keyof CheckoutAddressForm, value: string) => void
  validationByField: Map<string, string>
  disabled: boolean
}

const inputClassName =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-300/70 disabled:cursor-not-allowed disabled:bg-slate-100'

const requiredFields = new Set<keyof CheckoutAddressForm>(['firstName', 'lastName', 'line1', 'city', 'postalCode', 'country'])

const resolveErrorId = (formIdPrefix: string, field: keyof CheckoutAddressForm) => `${formIdPrefix}-${field}-error`
const resolveInputId = (formIdPrefix: string, field: keyof CheckoutAddressForm) => `${formIdPrefix}-${field}`

const AddressField = ({
  formIdPrefix,
  field,
  label,
  value,
  disabled,
  validationByField,
  onFieldChange,
  autoComplete,
}: {
  formIdPrefix: string
  field: keyof CheckoutAddressForm
  label: string
  value: string
  disabled: boolean
  validationByField: Map<string, string>
  onFieldChange: (field: keyof CheckoutAddressForm, value: string) => void
  autoComplete?: string
}) => {
  const errorMessage = validationByField.get(field)
  const inputId = resolveInputId(formIdPrefix, field)
  const errorId = resolveErrorId(formIdPrefix, field)
  const isRequired = requiredFields.has(field)

  return (
    <label className="space-y-1">
      <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
        {label}
        {isRequired ? '*' : ''}
      </span>
      <input
        id={inputId}
        className={inputClassName}
        value={value}
        disabled={disabled}
        required={isRequired}
        autoComplete={autoComplete}
        aria-invalid={Boolean(errorMessage)}
        aria-describedby={errorMessage ? errorId : undefined}
        onChange={(event) => onFieldChange(field, event.target.value)}
      />
      {errorMessage ? (
        <p id={errorId} className="text-xs text-rose-600">
          {errorMessage}
        </p>
      ) : null}
    </label>
  )
}

export const AddressFields = ({ form, formIdPrefix, onFieldChange, validationByField, disabled }: Props) => {
  const { t } = useShopI18n()

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <AddressField
        formIdPrefix={formIdPrefix}
        field="firstName"
        label={t('checkout.field.firstName')}
        value={form.firstName}
        disabled={disabled}
        validationByField={validationByField}
        onFieldChange={onFieldChange}
        autoComplete="given-name"
      />
      <AddressField
        formIdPrefix={formIdPrefix}
        field="lastName"
        label={t('checkout.field.lastName')}
        value={form.lastName}
        disabled={disabled}
        validationByField={validationByField}
        onFieldChange={onFieldChange}
        autoComplete="family-name"
      />

      <div className="sm:col-span-2">
        <AddressField
          formIdPrefix={formIdPrefix}
          field="company"
          label={t('checkout.field.company')}
          value={form.company}
          disabled={disabled}
          validationByField={validationByField}
          onFieldChange={onFieldChange}
          autoComplete="organization"
        />
      </div>

      <div className="sm:col-span-2">
        <AddressField
          formIdPrefix={formIdPrefix}
          field="line1"
          label={t('checkout.field.line1')}
          value={form.line1}
          disabled={disabled}
          validationByField={validationByField}
          onFieldChange={onFieldChange}
          autoComplete="address-line1"
        />
      </div>

      <div className="sm:col-span-2">
        <AddressField
          formIdPrefix={formIdPrefix}
          field="line2"
          label={t('checkout.field.line2')}
          value={form.line2}
          disabled={disabled}
          validationByField={validationByField}
          onFieldChange={onFieldChange}
          autoComplete="address-line2"
        />
      </div>

      <AddressField
        formIdPrefix={formIdPrefix}
        field="city"
        label={t('checkout.field.city')}
        value={form.city}
        disabled={disabled}
        validationByField={validationByField}
        onFieldChange={onFieldChange}
        autoComplete="address-level2"
      />
      <AddressField
        formIdPrefix={formIdPrefix}
        field="postalCode"
        label={t('checkout.field.postalCode')}
        value={form.postalCode}
        disabled={disabled}
        validationByField={validationByField}
        onFieldChange={onFieldChange}
        autoComplete="postal-code"
      />

      <AddressField
        formIdPrefix={formIdPrefix}
        field="region"
        label={t('checkout.field.region')}
        value={form.region}
        disabled={disabled}
        validationByField={validationByField}
        onFieldChange={onFieldChange}
        autoComplete="address-level1"
      />
      <AddressField
        formIdPrefix={formIdPrefix}
        field="country"
        label={t('checkout.field.country')}
        value={form.country}
        disabled={disabled}
        validationByField={validationByField}
        onFieldChange={onFieldChange}
        autoComplete="country"
      />

      <div className="sm:col-span-2">
        <AddressField
          formIdPrefix={formIdPrefix}
          field="phone"
          label={t('checkout.field.phone')}
          value={form.phone}
          disabled={disabled}
          validationByField={validationByField}
          onFieldChange={onFieldChange}
          autoComplete="tel"
        />
      </div>
    </div>
  )
}
